import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import {
  PushNotifications,
  type ActionPerformed,
  type PushNotificationSchema,
} from "@capacitor/push-notifications";
import { supabase } from "./lib/supabase";

export type MobilePushState = {
  status: "web" | "prompt" | "registering" | "registered" | "denied" | "unavailable" | "error";
  message: string;
};

export type MobilePushTarget = {
  tab?: string;
  missionId?: string;
  notificationId?: string;
};

type MobilePushContext = {
  onState: (state: MobilePushState) => void;
  onReceived: (notification: PushNotificationSchema) => void;
  onOpened: (target: MobilePushTarget) => void;
};

const APP_ID = "fr.ardclean.manager";
const TOKEN_STORAGE_KEY = `${APP_ID}:native-push-token`;
let activeContext: MobilePushContext | null = null;
let activeToken: string | null = null;

function storedToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function rememberToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // The native registration remains usable when WebView storage is unavailable.
  }
}

function targetFromAction(action: ActionPerformed): MobilePushTarget {
  const data = action.notification.data || {};
  return {
    tab: typeof data.tab === "string" ? data.tab : undefined,
    missionId:
      typeof data.mission_id === "string"
        ? data.mission_id
        : typeof data.missionId === "string"
          ? data.missionId
          : undefined,
    notificationId:
      typeof data.notification_id === "string"
        ? data.notification_id
        : typeof data.notificationId === "string"
          ? data.notificationId
          : undefined,
  };
}

async function persistRegistration(token: string) {
  const platform = Capacitor.getPlatform();
  if ((platform !== "ios" && platform !== "android") || !supabase) return;
  const previousToken = activeToken || storedToken();
  if (previousToken && previousToken !== token) {
    await supabase.rpc("unregister_mobile_push_devices", {
      p_device_token: previousToken,
    });
  }
  const { error } = await supabase.rpc("register_mobile_push_device", {
    p_platform: platform,
    p_device_token: token,
    p_app_id: APP_ID,
    p_device_label: navigator.userAgent.slice(0, 240),
    p_app_version: "1.0.0",
  });
  if (error) {
    const unavailable = /does not exist|schema cache|could not find the function/i.test(error.message);
    activeContext?.onState({
      status: unavailable ? "unavailable" : "error",
      message: unavailable
        ? "Le socle push est prêt dans l’application mais doit encore être activé sur la base principale."
        : `Inscription de ce téléphone impossible : ${error.message}`,
    });
    return;
  }
  activeToken = token;
  rememberToken(token);
  activeContext?.onState({
    status: "registered",
    message: "Notifications téléphone actives sur cet appareil.",
  });
}

async function prepareAndroidChannels() {
  if (Capacitor.getPlatform() !== "android") return;
  await Promise.all([
    PushNotifications.createChannel({
      id: "ard-alerts",
      name: "Alertes ARD Manager",
      description: "Urgences, actions bloquantes et changements importants.",
      importance: 5,
      visibility: 1,
      vibration: true,
    }),
    PushNotifications.createChannel({
      id: "ard-information",
      name: "Informations ARD Manager",
      description: "Missions, documents et informations de suivi.",
      importance: 3,
      visibility: 1,
      vibration: true,
    }),
  ]);
}

export async function initializeMobilePush(context: MobilePushContext) {
  activeContext = context;
  activeToken = storedToken();
  if (!Capacitor.isNativePlatform()) {
    context.onState({ status: "web", message: "Notifications internes actives dans le navigateur." });
    return () => undefined;
  }

  const listeners: PluginListenerHandle[] = [];
  listeners.push(
    await PushNotifications.addListener("registration", (token) => {
      void persistRegistration(token.value);
    }),
    await PushNotifications.addListener("registrationError", (error) => {
      context.onState({ status: "error", message: `Inscription native refusée : ${error.error}` });
    }),
    await PushNotifications.addListener("pushNotificationReceived", (notification) => {
      context.onReceived(notification);
    }),
    await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      context.onOpened(targetFromAction(action));
    }),
  );

  await prepareAndroidChannels();
  const permission = await PushNotifications.checkPermissions();
  if (permission.receive === "granted") {
    context.onState({ status: "registering", message: "Vérification de ce téléphone…" });
    await PushNotifications.register();
  } else {
    context.onState({
      status: permission.receive === "denied" ? "denied" : "prompt",
      message:
        permission.receive === "denied"
          ? "Les notifications téléphone sont refusées dans les réglages de l’appareil."
          : "Activez les notifications pour être informé même lorsque l’application est fermée.",
    });
  }

  return async () => {
    await Promise.all(listeners.map((listener) => listener.remove()));
    if (activeContext === context) activeContext = null;
  };
}

export async function requestMobilePushPermission() {
  if (!Capacitor.isNativePlatform() || !activeContext) return;
  activeContext.onState({ status: "registering", message: "Activation des notifications téléphone…" });
  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== "granted") {
    activeContext.onState({
      status: "denied",
      message: "Autorisation refusée. Vous pouvez la réactiver dans les réglages du téléphone.",
    });
    return;
  }
  await PushNotifications.register();
}

export async function deactivateMobilePush() {
  if (!Capacitor.isNativePlatform()) return;
  const token = activeToken || storedToken();
  if (supabase) {
    await supabase.rpc("unregister_mobile_push_devices", {
      p_device_token: token,
    });
  }
  await PushNotifications.unregister();
  activeToken = null;
  rememberToken(null);
  activeContext?.onState({
    status: "prompt",
    message: "Notifications téléphone désactivées sur cet appareil.",
  });
}
